import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { QrScanner } from './components/QrScanner';
import { ArrowRightIcon, BusIcon, CheckIcon, ClipboardIcon, LockIcon, QrIcon, SignalIcon, WifiIcon } from './components/icons';
import { portalApi } from './lib/api';
import { externalBrowserHref, isExternalBrowserHandoff, normalizeCode, replaceCaptiveContext, resolveCaptiveContext, resolveCaptiveRenewUrl, resolvePresetCode, resolvePresetNas } from './lib/code';
import { consumeNativeQrCode, prepareNativeQrHandoff } from './lib/native-qr-handoff';
import { successDestination } from './lib/navigation';
import { redemptionPresentation } from './lib/redemption-status';
import { ApiError, type PublicBusContext, type RedemptionInput, type RedemptionResult } from './types';

type Step = 'code' | 'success';

const CAPTIVE_SPEED_PROFILE = (import.meta.env.VITE_CAPTIVE_SPEED_PROFILE as string | undefined) ?? 'balanced';
const STATUS_POLL_INTERVAL_MS = CAPTIVE_SPEED_PROFILE === 'fast' ? 250 : CAPTIVE_SPEED_PROFILE === 'conservative' ? 1_000 : 500;
const STATUS_POLL_TIMEOUT_MS = 90_000;
const REDIRECT_DELAY_SECONDS = 1;

const DEFAULT_CONTEXT: PublicBusContext = {
  nasIdentifier: '',
  captiveLoginUrl: 'http://10.81.1.1/login',
  bus: { id: 0, nombre: 'Tu bus', totalAsientos: 0, pisos: 1 },
  empresa: { nombre: 'PAKDA', slug: 'pakda', colorPrimario: '#69229A', colorSecundario: '#F06A22' },
};

export default function App() {
  const [context, setContext] = useState<PublicBusContext>(DEFAULT_CONTEXT);
  const [contextReady, setContextReady] = useState(false);
  const [pendingQrCode, setPendingQrCode] = useState('');
  const [fatalError, setFatalError] = useState('');
  const [step, setStep] = useState<Step>('code');
  const [code, setCode] = useState(() => {
    const initialInput = document.getElementById('boot-code');
    return initialInput instanceof HTMLInputElement ? normalizeCode(initialInput.value) : '';
  });
  const [codeSource, setCodeSource] = useState<'manual' | 'qr'>('manual');
  const [scannerOpen, setScannerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [result, setResult] = useState<RedemptionResult>();
  const [pollingMessage, setPollingMessage] = useState('');
  const [redirectIn, setRedirectIn] = useState(REDIRECT_DELAY_SECONDS);
  const activationLock = useRef(false);
  const presetCodeHandled = useRef(false);
  const bootCodeHandled = useRef(false);
  const bootCodeComplete = useRef(code.length === 6);

  const renewCaptiveContext = useCallback(async () => {
    const renewalPath = resolveCaptiveRenewUrl();
    if (!renewalPath) throw new Error('Esta conexión no permite renovar la autorización.');
    const renewed = await portalApi.renewContext(renewalPath);
    replaceCaptiveContext(renewed.context, renewed.renew);
    return renewed.context;
  }, []);

  const loadContext = useCallback(async () => {
    setFatalError('');
    setContextReady(false);
    let contextToken = resolveCaptiveContext();
    if (!contextToken) {
      const presetNas = resolvePresetNas();
      const presetCode = resolvePresetCode();
      if (presetNas && presetCode) {
        try {
          const presetBus = await portalApi.getBusByNas(presetNas);
          window.location.replace(prepareNativeQrHandoff(presetNas, presetCode, presetBus.captiveLoginUrl));
          return;
        } catch {
          setFatalError('No pudimos abrir el acceso de este bus. Conéctate al WiFi WayBus e inténtalo de nuevo.');
          return;
        }
      }
    }
    if (!contextToken && !import.meta.env.DEV) {
      setFatalError('Esta autorización no pertenece a una conexión WiFi activa. Desconéctate de la red y vuelve a conectarte.');
      return;
    }
    try {
      const loadedContext = await portalApi.getBus(contextToken || 'mock');
      setContext(loadedContext);
      setPendingQrCode(consumeNativeQrCode(loadedContext.nasIdentifier));
      setContextReady(true);
    } catch (error) {
      if (error instanceof ApiError && error.code === 'CAPTIVE_CONTEXT_INVALID') {
        try {
          const renewedToken = await renewCaptiveContext();
          const loadedContext = await portalApi.getBus(renewedToken);
          setContext(loadedContext);
          setPendingQrCode(consumeNativeQrCode(loadedContext.nasIdentifier));
          setContextReady(true);
          return;
        } catch {
          setFatalError('Tu acceso seguro venció y ya no pudimos reconocer este teléfono. Apaga y enciende el WiFi para volver a abrir el portal.');
          return;
        }
      }
      setFatalError('No pudimos verificar este punto WiFi. Comprueba que sigues conectado a la red del bus e inténtalo otra vez.');
    }
  }, [renewCaptiveContext]);

  useEffect(() => { void loadContext(); }, [loadContext]);

  useEffect(() => {
    if (!context) return;
    document.documentElement.style.setProperty('--brand-primary', context.empresa.colorPrimario);
    document.documentElement.style.setProperty('--brand-accent', context.empresa.colorSecundario);
    document.title = `WiFi a bordo · ${context.empresa.nombre}`;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', context.empresa.colorPrimario);
  }, [context]);

  useEffect(() => {
    if (!result || result.status !== 'PENDING') return;
    let cancelled = false;
    let timer: number | undefined;
    const startedAt = Date.now();
    let pollAttempts = 0;
    const poll = async () => {
      pollAttempts += 1;
      try {
        const next = await portalApi.activationStatus(result.activationId, result.statusToken);
        if (cancelled) return;
        setResult((current) => current ? { ...current, ...next } : current);
        if (next.status !== 'PENDING') return;
      } catch {
        if (!cancelled) setPollingMessage('Seguimos preparando tu conexión. Mantén esta pantalla abierta.');
      }
      if (!cancelled && Date.now() - startedAt < STATUS_POLL_TIMEOUT_MS) {
        const backoff = Math.min(2_000, STATUS_POLL_INTERVAL_MS * (1 + Math.floor(pollAttempts / 8)));
        const jitter = Math.floor(Math.random() * Math.min(150, backoff / 4));
        timer = window.setTimeout(() => void poll(), backoff + jitter);
      } else if (!cancelled) {
        setPollingMessage('La conexión está tardando más de lo esperado. Puedes intentarlo nuevamente o pedir ayuda a la tripulación.');
      }
    };
    timer = window.setTimeout(() => void poll(), STATUS_POLL_INTERVAL_MS);
    return () => { cancelled = true; if (timer) window.clearTimeout(timer); };
  }, [result?.activationId, result?.status, result?.statusToken]);

  const destination = useMemo(() => successDestination(result?.redirectUrl), [result?.redirectUrl]);

  useEffect(() => {
    if (result?.status !== 'ACTIVATED') return;
    setRedirectIn(REDIRECT_DELAY_SECONDS);
    const interval = window.setInterval(() => setRedirectIn((value) => Math.max(0, value - 1)), 1_000);
    const timeout = window.setTimeout(() => window.location.assign(destination), REDIRECT_DELAY_SECONDS * 1_000);
    return () => { window.clearInterval(interval); window.clearTimeout(timeout); };
  }, [destination, result?.status]);

  const activate = useCallback(async (value: string, source: 'manual' | 'qr') => {
    const normalized = source === 'manual' ? normalizeCode(value) : value.trim();
    if (!contextReady || activationLock.current || (source === 'manual' ? normalized.length !== 6 : normalized.length < 6 || normalized.length > 255)) return;
    activationLock.current = true;
    setCode(normalized);
    setCodeSource(source);
    setScannerOpen(false);
    const contextToken = resolveCaptiveContext();
    if (!contextToken && !import.meta.env.DEV) {
      setSubmitError('La autorización venció. Desconéctate del WiFi y vuelve a conectarte para obtener una nueva.');
      activationLock.current = false;
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    setPollingMessage('');
    const input: RedemptionInput = { context: contextToken || 'mock', codigo: normalized };
    try {
      let response: RedemptionResult;
      try {
        response = await portalApi.redeem(input);
      } catch (error) {
        if (!(error instanceof ApiError) || error.code !== 'CAPTIVE_CONTEXT_INVALID' || import.meta.env.DEV) throw error;
        setPollingMessage('Actualizando tu acceso seguro…');
        response = await portalApi.redeem({ ...input, context: await renewCaptiveContext() });
      }
      setPollingMessage('');
      setResult(response);
      setStep('success');
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'No pudimos activar tu conexión. Revisa el código e inténtalo otra vez.');
    } finally {
      setSubmitting(false);
      activationLock.current = false;
    }
  }, [contextReady, renewCaptiveContext]);

  const acceptManualCode = useCallback((value: string) => {
    const normalized = normalizeCode(value);
    setCode(normalized);
    setCodeSource('manual');
    setSubmitError('');
    if (normalized.length === 6 && contextReady) void activate(normalized, 'manual');
  }, [activate, contextReady]);

  const acceptQrCode = useCallback((value: string) => { void activate(value, 'qr'); }, [activate]);

  async function pasteCode() {
    try { acceptManualCode(await navigator.clipboard.readText()); }
    catch { setSubmitError('No pudimos leer el portapapeles. Mantén presionado el campo y elige Pegar.'); }
  }

  useEffect(() => {
    if (presetCodeHandled.current || !contextReady || fatalError || step !== 'code') return;
    const preset = resolvePresetCode() || pendingQrCode;
    if (!preset) return;
    presetCodeHandled.current = true;
    void activate(preset, 'qr');
  }, [activate, contextReady, fatalError, pendingQrCode, step]);

  useEffect(() => {
    if (bootCodeHandled.current || !bootCodeComplete.current || !contextReady || fatalError || step !== 'code' || code.length !== 6 || codeSource !== 'manual') return;
    bootCodeHandled.current = true;
    void activate(code, 'manual');
  }, [activate, code, codeSource, contextReady, fatalError, step]);

  if (fatalError) return <StatusPage variant="error"><div className="status-symbol">!</div><h1>No pudimos abrir tu acceso</h1><p>{fatalError}</p><button className="primary-button" onClick={() => void loadContext()}>Intentar otra vez</button><small>Si continúa, avisa a la tripulación.</small></StatusPage>;

  const brandStyle = {
    '--brand-primary': context.empresa.colorPrimario,
    '--brand-accent': context.empresa.colorSecundario,
  } as CSSProperties;
  const resultView = result ? redemptionPresentation(result.status, context.empresa.nombre) : undefined;
  const browserHref = externalBrowserHref();
  const browserLabel = browserHref.startsWith('intent:') ? 'Abrir en Chrome' : 'Abrir en navegador completo';
  const externalHandoff = isExternalBrowserHandoff();

  return <main className="portal-shell" style={brandStyle}>
    <div className="brand-rail" aria-hidden="true" />
    <header className="brand-header">
      <BrandLogo context={context} />
      <div className="secure-label"><SignalIcon /><span>WiFi a bordo</span></div>
    </header>

    <section className="journey-strip" aria-label="Información del viaje">
      <div className="journey-icon"><BusIcon /></div>
      <div className="journey-copy"><strong>{context.bus.ruta ?? 'Viaje en curso'}</strong><span>{context.bus.nombre}</span></div>
      <div className="live-state"><i /> Disponible</div>
    </section>

    {step === 'code' && <section className="portal-content" aria-labelledby="connection-title">
      <h1 id="connection-title">Tu viaje también va conectado.</h1>
      <p className="lead">{externalHandoff ? 'Escanea el QR de tu asiento o escribe el código. No necesitas completar ningún dato adicional.' : 'Escribe el código de tu asiento. No necesitas completar ningún dato adicional.'}</p>

      {externalHandoff && <button className="scan-button" onClick={() => setScannerOpen(true)}>
        <span className="scan-icon"><QrIcon /></span>
        <span className="scan-copy"><strong>{submitting && codeSource === 'qr' ? 'Conectando…' : 'Escanear y conectarme'}</strong><small>El acceso se activa automáticamente</small></span>
        <span className="button-arrow" aria-hidden="true"><ArrowRightIcon /></span>
      </button>}

      {externalHandoff && <div className="method-divider"><span>También puedes escribirlo</span></div>}
      <label className="field-label" htmlFor="code">Código de 6 caracteres</label>
      <div className="code-row">
        <input id="code" className="code-input" value={codeSource === 'manual' ? code : ''} disabled={submitting} onChange={(event) => acceptManualCode(event.target.value)} placeholder="ABC123" autoCapitalize="characters" autoComplete="one-time-code" inputMode="text" maxLength={6} aria-describedby={submitError ? 'code-error' : undefined} />
        <button onClick={() => void pasteCode()} type="button" aria-label="Pegar código"><ClipboardIcon /><span>Pegar</span></button>
      </div>
      {submitError && <p id="code-error" className="field-error" role="alert">{submitError}</p>}
      <button className="primary-button connect-button" disabled={!contextReady || submitting || codeSource !== 'manual' || code.length !== 6} onClick={() => void activate(code, 'manual')}>{submitting ? <><span className="button-spinner" /> Conectando…</> : <><WifiIcon /> Conectarme</>}</button>
      <div className="trust-note"><LockIcon /><span>El código identifica tu asiento. No solicitamos nombre ni celular.</span></div>
    </section>}

    {step === 'success' && result && resultView && <section className="portal-content result-content" aria-live="polite">
      <div className={`result-symbol ${resultView.icon}`}><span>{resultView.icon === 'success' ? <CheckIcon /> : resultView.icon === 'error' ? '!' : <WifiIcon />}</span></div>
      <p className="result-state">{resultView.label}</p>
      <h1>{resultView.title}</h1>
      <p className="lead">{resultView.description}</p>

      {result.status === 'PENDING' && <div className="activation-progress"><span /><p>{pollingMessage || 'Mantén esta pantalla abierta.'}</p></div>}

      {result.status !== 'PENDING' && <div className="trip-summary">
        <SummaryItem label={result.crewPosition ? "Puesto" : "Asiento"} value={result.crewPosition ?? String(result.asiento)} featured />
        <SummaryItem label="Bus" value={result.bus} />
        <SummaryItem label="Ruta" value={result.ruta ?? context.bus.ruta ?? 'En curso'} wide />
        <SummaryItem label="Servicio" value={result.plan} wide />
        {result.status === 'ACTIVATED' && result.accessExpiresAt && <SummaryItem label="Acceso válido hasta" value={new Date(result.accessExpiresAt).toLocaleString('es-PE', { dateStyle: 'short', timeStyle: 'short' })} wide />}
      </div>}

      {result.posibleConflicto && <p className="notice">Otro pasajero indicó este asiento. Tu solicitud sigue registrada y la tripulación puede revisarla.</p>}
      {resultView.canNavigate && <>
        <a className="primary-button link-button" href={destination}>Empezar a navegar</a>
        <p className="redirect-note">Abriremos internet automáticamente en {redirectIn} s.</p>
      </>}
      {result.status === 'FAILED' && <button className="secondary-button" onClick={() => { setResult(undefined); setStep('code'); }}>Probar con otro código</button>}
      <p className="help-note">¿Necesitas ayuda? Muéstrale esta pantalla a la tripulación.</p>
    </section>}

    {scannerOpen && <QrScanner brandName={context.empresa.nombre} browserHref={browserHref} browserLabel={browserLabel} showBrowserHandoff={!externalHandoff} onCode={acceptQrCode} onClose={() => setScannerOpen(false)} />}
    <footer><span>Conectividad gestionada por</span><strong>PAKDA</strong></footer>
  </main>;
}

function BrandLogo({ context }: { context: PublicBusContext }) {
  return context.empresa.logoUrl
    ? <img className="brand-logo" src={context.empresa.logoUrl} alt={`Logo de ${context.empresa.nombre}`} width="404" height="132" fetchPriority="high" />
    : <div className="brand-fallback" aria-label={context.empresa.nombre}>{context.empresa.nombre.slice(0, 2).toUpperCase()}</div>;
}

function SummaryItem({ label, value, featured = false, wide = false }: { label: string; value: string; featured?: boolean; wide?: boolean }) {
  return <div className={`${featured ? 'featured' : ''} ${wide ? 'wide' : ''}`}><span>{label}</span><strong>{value}</strong></div>;
}

function StatusPage({ children, variant }: { children: ReactNode; variant: 'loading' | 'error' }) {
  return <main className={`status-page ${variant}`}>{children}<div className="status-brand">PAKDA</div></main>;
}
